import { useState, useMemo, useCallback } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) {
    return <ArrowUpDown className="ml-1 inline h-3 w-3 text-muted-foreground/50" />;
  }
  return sortDir === 'asc' ? <ArrowUp className="ml-1 inline h-3 w-3" /> : <ArrowDown className="ml-1 inline h-3 w-3" />;
};

const SlowestRoutesTable = ({ data }) => {
  const [sortField, setSortField] = useState('avg_duration_ms');
  const [sortDir, setSortDir] = useState('desc');

  const maxCount = useMemo(() => Math.max(...data.map((r) => r.request_count), 1), [data]);

  const sorted = useMemo(() => {
    const arr = [...data];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'route' || sortField === 'method') {
        cmp = (a[sortField] || '').localeCompare(b[sortField] || '');
      } else {
        cmp = (a[sortField] || 0) - (b[sortField] || 0);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [data, sortField, sortDir]);

  const toggleSort = useCallback(
    (field) => {
      if (sortField === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDir('desc');
      }
    },
    [sortField]
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Slowest Routes</CardTitle>
        <CardDescription>Aggregated by route, sorted by average duration</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('route')}>
                  Route <SortIcon field="route" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('method')}>
                  Method <SortIcon field="method" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort('request_count')}>
                  Requests <SortIcon field="request_count" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort('avg_duration_ms')}>
                  Avg Duration <SortIcon field="avg_duration_ms" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort('p95_duration_ms')}>
                  P95 Duration <SortIcon field="p95_duration_ms" sortField={sortField} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort('error_rate')}>
                  Error Rate <SortIcon field="error_rate" sortField={sortField} sortDir={sortDir} />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((row) => (
                <TableRow key={row.route}>
                  <TableCell className="font-mono text-xs">{row.route}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs font-medium">
                      {row.method}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-sm tabular-nums">{row.request_count.toLocaleString()}</span>
                      <div className="h-1.5 w-16 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full rounded-full bg-foreground/20" style={{ width: `${(row.request_count / maxCount) * 100}%` }} />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{row.avg_duration_ms}ms</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{row.p95_duration_ms}ms</TableCell>
                  <TableCell className="text-right">
                    <Badge variant={row.error_rate > 5 ? 'destructive' : 'secondary'} className="text-xs">
                      {row.error_rate.toFixed(1)}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default SlowestRoutesTable;
